import { normalizePhoneToE164 } from '../normalizePhoneToE164.js';
import { maskPhone } from '../maskPhone.js';
import {
  INBOUND_CALLS_DEFAULTS,
  type InboundCallLeg,
  type InboundCallOutcome,
  type InboundCallsSettings,
  type CallsSettings,
} from '../../types/speedToLead.js';
import {
  renderMissed,
  renderWhisper,
  validateMessageTemplate,
  type MessageKind,
} from './spokenMessages.js';

// INBOUND CALL FORWARDING (Stage 2.5): settings validation and the routing PLAN. PURE: the server gathers
// the facts (pool, conversations, intake links, settings, staff, hours) and this decides. No I/O, no
// clock (staffed-now is a fact the caller computes).
//
// ⚠️ OFF BY DEFAULT. A club forwards only when `inboundCalls.enabled` is true AND it has a primary number;
// an unknown caller on a shared pool forwards only when the POOL has a primary number (setting it is that
// pool's switch). Anything else is the fixed message, a log and a staff alert.
// ⚠️ NO RECORDING of any kind, anywhere in this file or the NCCO builders (a source guard scans for it).

export const INBOUND_CALLS_LIMITS = { ringSeconds: { min: 10, max: 60 } } as const;

const digits = (v: string) => v.replace(/[^0-9]/g, '');

export { maskPhone };

function storedTemplate(kind: MessageKind, raw: unknown): string | null {
  const c = validateMessageTemplate(kind, raw);
  return c.ok ? c.value : null;
}

/** The read boundary: anything missing or garbage resolves to the DEFAULT (off). */
export function resolveInboundCalls(raw: unknown): InboundCallsSettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const L = INBOUND_CALLS_LIMITS.ringSeconds;
  const ring = r.ringSeconds;
  return {
    enabled: r.enabled === true,
    primaryNumber: typeof r.primaryNumber === 'string' && r.primaryNumber.trim() !== '' ? r.primaryNumber.trim() : null,
    thenStaffList: r.thenStaffList === true,
    ringSeconds:
      typeof ring === 'number' && Number.isInteger(ring) && ring >= L.min && ring <= L.max
        ? ring
        : INBOUND_CALLS_DEFAULTS.ringSeconds,
    respectStaffedHours: r.respectStaffedHours === false ? false : INBOUND_CALLS_DEFAULTS.respectStaffedHours,
    // ⚠️ A stored template that no longer validates (hand-edited) reads as the DEFAULT, never as markup/links.
    whisperMessage: storedTemplate('whisper', r.whisperMessage),
    missedCallMessage: storedTemplate('missed', r.missedCallMessage),
    messagesLockedByKyck: r.messagesLockedByKyck === true,
  };
}

export type PrimaryNumberCheck = { ok: true; value: string | null } | { ok: false; error: string };

/** A primary number: empty is fine (none set); otherwise an AU E.164 mobile or landline. */
export function normalisePrimaryNumber(raw: unknown): PrimaryNumberCheck {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, error: 'The primary number must be a phone number or empty.' };
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const e164 = normalizePhoneToE164(trimmed, 'AU');
  if (!e164 || !e164.startsWith('+61')) {
    return { ok: false, error: `"${trimmed}" is not an Australian mobile or landline number we can ring.` };
  }
  return { ok: true, value: e164 };
}

/**
 * ⚠️ A primary number must never be one of OUR numbers (a Vonage or Twilio number, or a pool number):
 * forwarding a call to a number that forwards back is a loop. `ownDigits` is gathered by the server.
 */
export function ownNumberError(e164: string | null, ownDigits: ReadonlySet<string>): string | null {
  if (e164 === null) return null;
  return ownDigits.has(digits(e164))
    ? `${e164} is one of our own numbers. Forwarding to it would loop the call; use the club's own phone.`
    : null;
}

export type InboundCallsShape = { ok: true; value: InboundCallsSettings } | { ok: false; error: string };

/** Structure of the `inboundCalls` block of a settings write (strict: no coercion). */
export function validateInboundCallsShape(input: unknown): InboundCallsShape {
  if (!input || typeof input !== 'object') return { ok: false, error: 'inboundCalls must be an object.' };
  const i = input as Record<string, unknown>;
  const L = INBOUND_CALLS_LIMITS.ringSeconds;
  if (typeof i.enabled !== 'boolean') return { ok: false, error: 'inboundCalls.enabled must be true or false.' };
  if (typeof i.thenStaffList !== 'boolean') return { ok: false, error: 'inboundCalls.thenStaffList must be true or false.' };
  if (typeof i.respectStaffedHours !== 'boolean') return { ok: false, error: 'inboundCalls.respectStaffedHours must be true or false.' };
  if (typeof i.ringSeconds !== 'number' || !Number.isInteger(i.ringSeconds) || i.ringSeconds < L.min || i.ringSeconds > L.max) {
    return { ok: false, error: `Ring time must be a whole number of seconds from ${L.min} to ${L.max}.` };
  }
  const number = normalisePrimaryNumber(i.primaryNumber);
  if (!number.ok) return number;
  // The two spoken messages and the Kyck lock are OPTIONAL in a write: a block that omits them keeps the stored
  // ones (the service merges), so an older form can never reset a club's wording or lift the lock.
  if (i.messagesLockedByKyck !== undefined && typeof i.messagesLockedByKyck !== 'boolean') {
    return { ok: false, error: 'inboundCalls.messagesLockedByKyck must be true or false.' };
  }
  const w = i.whisperMessage === undefined ? undefined : validateMessageTemplate('whisper', i.whisperMessage);
  if (w && !w.ok) return w;
  const m = i.missedCallMessage === undefined ? undefined : validateMessageTemplate('missed', i.missedCallMessage);
  if (m && !m.ok) return m;
  return {
    ok: true,
    value: {
      enabled: i.enabled,
      primaryNumber: number.value,
      thenStaffList: i.thenStaffList,
      ringSeconds: i.ringSeconds,
      respectStaffedHours: i.respectStaffedHours,
      // undefined (omitted) is turned into the stored value by the service; the type here is the full shape.
      whisperMessage: (w && w.ok ? w.value : undefined) as string | null,
      missedCallMessage: (m && m.ok ? m.value : undefined) as string | null,
      messagesLockedByKyck: (i.messagesLockedByKyck === undefined ? undefined : i.messagesLockedByKyck) as boolean,
    },
  };
}

// ---------------------------------------------------------------------------
// the routing plan
// ---------------------------------------------------------------------------

export interface InboundStaffFact {
  teamMemberId: string;
  name: string;
  /** E.164, or null when the team member has no readable mobile. */
  mobile: string | null;
  /** The club's staff list switch (`receivesLeadCalls`). */
  enabled: boolean;
}

export interface InboundClubFact {
  accountId: string;
  /** The name spoken to the caller. */
  name: string;
  inboundCalls: InboundCallsSettings;
  /** The club's Calls settings: whether an ACCEPTED inbound call is recorded and transcribed. */
  calls: CallsSettings;
  /** Enabled-or-not entries in the club's saved order. */
  staff: InboundStaffFact[];
  /** True when the club is staffed right now, or its hours are unreadable (never block on a guess). */
  staffedNow: boolean;
}

export interface InboundCallFacts {
  /** The pool this call's `To` number belongs to; null = a number no pool owns. */
  pool: { id: string; name: string; primaryNumber: string | null; groupStaff: Array<{ name: string; mobile: string }> } | null;
  /** The club the call is for, when it could be worked out (a known caller, or a dedicated pool). */
  club: InboundClubFact | null;
  /** The caller has a conversation or an intake lead in this pool: they are a known lead. */
  callerKnown: boolean;
  /** The lead's first name, for the whisper. Null when unknown. */
  leadFirstName: string | null;
  /** A readable label for how the lead arrived ("Meta lead form"), for the whisper's {source}. Null when unknown. */
  leadSource?: string | null;
}

export interface InboundCallPlan {
  /** Null = ring the legs. Otherwise nothing rings and the fixed message plays. */
  outcome: Exclude<InboundCallOutcome, 'answered_primary' | 'answered_staff' | 'no_answer'> | null;
  legs: InboundCallLeg[];
  ringSeconds: number;
  whisper: string | null;
  /** What the caller hears if nothing is answered. */
  fixedMessage: string;
  accountId: string | null;
}

export const DEFAULT_RING_SECONDS = INBOUND_CALLS_DEFAULTS.ringSeconds;

/**
 * What the caller hears when nobody takes the call: the club's own template, else the default wording
 * ("Thanks for calling {club}. Sorry we missed you. ..."). The name is the club's, else the pool's.
 */
export function fixedMessageFor(name: string, template: string | null = null): string {
  return renderMissed(template, name);
}

/**
 * The whisper heard by whoever answers (the club's template, else the default), WITHOUT the key prompt:
 * `spokenWhisper` appends "Press any key to take the call." when the NCCO is built, so no template can drop it.
 */
export function whisperFor(input: {
  leadFirstName: string | null;
  clubName: string | null;
  poolName: string | null;
  leadSource?: string | null;
  template?: string | null;
  callerKnown?: boolean;
}): string {
  return renderWhisper(input.template ?? null, {
    leadName: input.leadFirstName,
    club: input.clubName ?? input.poolName,
    source: input.leadSource ?? null,
    pool: input.poolName,
    callerKnown: input.callerKnown,
  });
}

function dedupeLegs(legs: InboundCallLeg[]): InboundCallLeg[] {
  const seen = new Set<string>();
  return legs.filter((l) => {
    const d = digits(l.number);
    if (seen.has(d)) return false;
    seen.add(d);
    return true;
  });
}

export function planInboundCall(facts: InboundCallFacts): InboundCallPlan {
  const { pool, club } = facts;
  const base = { legs: [] as InboundCallLeg[], ringSeconds: DEFAULT_RING_SECONDS, whisper: null as string | null };

  // 1. A number no pool owns: the fixed message (the caller logs an ERROR).
  if (!pool) return { ...base, outcome: 'unrouted', fixedMessage: fixedMessageFor(''), accountId: null };

  const poolName = pool.name;
  const whisper = whisperFor({
    leadFirstName: facts.leadFirstName,
    clubName: club?.name ?? null,
    poolName,
    leadSource: facts.leadSource ?? null,
    template: club?.inboundCalls.whisperMessage ?? null,
    callerKnown: facts.callerKnown,
  });

  if (club) {
    const s = club.inboundCalls;
    const fixedMessage = fixedMessageFor(club.name, s.missedCallMessage);
    if (!s.enabled || !s.primaryNumber) return { ...base, outcome: 'disabled', fixedMessage, accountId: club.accountId };
    if (s.respectStaffedHours && !club.staffedNow) return { ...base, outcome: 'out_of_hours', fixedMessage, accountId: club.accountId };
    const legs: InboundCallLeg[] = [{ kind: 'primary', number: s.primaryNumber, teamMemberId: null }];
    if (s.thenStaffList) {
      for (const m of club.staff) {
        if (m.enabled && m.mobile) legs.push({ kind: 'staff', number: m.mobile, teamMemberId: m.teamMemberId });
      }
    }
    return { outcome: null, legs: dedupeLegs(legs), ringSeconds: s.ringSeconds, whisper, fixedMessage, accountId: club.accountId };
  }

  // 2. An unknown caller on a shared pool: the pool's primary number, then its group staff.
  const fixedMessage = fixedMessageFor(poolName);
  if (!pool.primaryNumber) return { ...base, outcome: 'disabled', fixedMessage, accountId: null };
  const legs: InboundCallLeg[] = [{ kind: 'primary', number: pool.primaryNumber, teamMemberId: null }];
  for (const s of pool.groupStaff) {
    const e164 = normalizePhoneToE164(s.mobile, 'AU');
    if (e164) legs.push({ kind: 'group_staff', number: e164, teamMemberId: null });
  }
  return { outcome: null, legs: dedupeLegs(legs), ringSeconds: DEFAULT_RING_SECONDS, whisper, fixedMessage, accountId: null };
}

/** Whether a leg's terminal status means "nobody took it" (move to the next target). */
export const LEG_NOT_ANSWERED_STATUSES: readonly string[] = ['unanswered', 'busy', 'timeout', 'failed', 'rejected', 'cancelled', 'machine'];

export function isLegNotAnswered(status: string): boolean {
  return LEG_NOT_ANSWERED_STATUSES.includes(status);
}

/** "3m 12s" / "45s". */
export function formatCallDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}
